import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../App';

const ITEMS_PER_LOAD = 50;

function Companies() {
  const [allCompanies, setAllCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});
  const [visibleCounts, setVisibleCounts] = useState({});
  const [expandedCompany, setExpandedCompany] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    companyType: '',
    founderCountMin: '',
    founderCountMax: '',
    dateFrom: '',
    dateTo: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      // Load all companies (no pagination from server - we handle it client-side)
      const response = await api.get('/companies', { params: { limit: 10000 } });
      setAllCompanies(response.data.companies);
    } catch (err) {
      console.error('Failed to load companies:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  // Get unique company types for filter dropdown
  const companyTypes = [...new Set(allCompanies.map(c => c.company_type).filter(Boolean))].sort();

  // Apply filters and search
  const filteredCompanies = allCompanies.filter(c => {
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        c.company_name?.toLowerCase().includes(searchLower) ||
        c.company_number?.includes(search) ||
        c.address?.toLowerCase().includes(searchLower) ||
        c.founders?.some(f => {
          const name = typeof f === 'string' ? f : f.name;
          return name?.toLowerCase().includes(searchLower);
        });
      if (!matchesSearch) return false;
    }

    // Company type filter
    if (filters.companyType && c.company_type !== filters.companyType) {
      return false;
    }

    // Founder count filter (range)
    const founderCount = c.founders?.length || 0;
    if (filters.founderCountMin !== '') {
      const min = parseInt(filters.founderCountMin, 10);
      if (!isNaN(min) && founderCount < min) return false;
    }
    if (filters.founderCountMax !== '') {
      const max = parseInt(filters.founderCountMax, 10);
      if (!isNaN(max) && founderCount > max) return false;
    }

    // Date range filter
    if (filters.dateFrom || filters.dateTo) {
      const scrapeDate = c.scrapeDate ? c.scrapeDate.split('T')[0] : null;
      if (!scrapeDate) return false;
      if (filters.dateFrom && scrapeDate < filters.dateFrom) return false;
      if (filters.dateTo && scrapeDate > filters.dateTo) return false;
    }

    return true;
  });

  // Group companies by scrapeDate
  const groupedCompanies = filteredCompanies.reduce((groups, company) => {
    const date = company.scrapeDate || 'Unknown Date';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(company);
    return groups;
  }, {});

  // Sort dates descending (newest first)
  const sortedDates = Object.keys(groupedCompanies).sort((a, b) => {
    if (a === 'Unknown Date') return 1;
    if (b === 'Unknown Date') return -1;
    return new Date(b) - new Date(a);
  });

  // Initialize expanded states for new dates
  useEffect(() => {
    const newExpandedDates = {};
    const newVisibleCounts = {};
    sortedDates.forEach((date, index) => {
      // Expand first date by default
      if (expandedDates[date] === undefined) {
        newExpandedDates[date] = index === 0;
      }
      // Initialize visible count
      if (visibleCounts[date] === undefined) {
        newVisibleCounts[date] = ITEMS_PER_LOAD;
      }
    });
    if (Object.keys(newExpandedDates).length > 0) {
      setExpandedDates(prev => ({ ...prev, ...newExpandedDates }));
    }
    if (Object.keys(newVisibleCounts).length > 0) {
      setVisibleCounts(prev => ({ ...prev, ...newVisibleCounts }));
    }
  }, [sortedDates.join(',')]);

  const toggleDate = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const loadMore = (date) => {
    setVisibleCounts(prev => ({
      ...prev,
      [date]: (prev[date] || ITEMS_PER_LOAD) + ITEMS_PER_LOAD
    }));
  };

  const formatDateHeader = (dateStr) => {
    if (dateStr === 'Unknown Date') return 'Unknown Date';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nl-BE', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const clearFilters = () => {
    setFilters({ companyType: '', founderCountMin: '', founderCountMax: '', dateFrom: '', dateTo: '' });
    setSearch('');
  };

  const activeFilterCount = [
    filters.companyType,
    filters.founderCountMin,
    filters.founderCountMax,
    filters.dateFrom,
    filters.dateTo,
    search
  ].filter(v => v !== '').length;

  const parseAddress = (address) => {
    if (!address) return { street: '', houseNumber: '', postalCode: '', city: '' };

    // Belgian format: STREETNAME HOUSENUMBER POSTALCODE(4 digits) CITY
    const match = address.match(/^(.+)\s+(\d{4})\s+(.+)$/);
    if (!match) return { street: address, houseNumber: '', postalCode: '', city: '' };

    const beforePostal = match[1].trim();
    const postalCode = match[2];
    const city = match[3].trim();

    // Split street name from house number: house number starts at first digit-leading token
    const parts = beforePostal.split(/\s+/);
    const houseIdx = parts.findIndex(p => /^\d/.test(p));
    if (houseIdx > 0) {
      return {
        street: parts.slice(0, houseIdx).join(' '),
        houseNumber: parts.slice(houseIdx).join(' '),
        postalCode,
        city,
      };
    }

    return { street: beforePostal, houseNumber: '', postalCode, city };
  };

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const exportCsv = (sortOrder) => {
    if (filteredCompanies.length === 0) return;
    setShowExportMenu(false);

    // Sort by scrapeDate
    const sorted = [...filteredCompanies].sort((a, b) => {
      const dateA = a.scrapeDate || '';
      const dateB = b.scrapeDate || '';
      return sortOrder === 'asc'
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    });

    const SEP = ';';
    const headers = ['Company Name', 'Street Name', 'House Number', 'Postal Code', 'City', 'Company Number', 'Company Type', 'Founders', 'Founder Count', 'Scrape Date'];
    const escapeField = (val) => {
      if (val == null) return '';
      const str = String(val);
      if (str.includes(SEP) || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const rows = sorted.map(c => {
      const founders = (c.founders || []).map(f => {
        const name = typeof f === 'string' ? f : f.name;
        const title = typeof f === 'string' ? '' : f.title;
        return title ? `${title} ${name}` : name;
      }).join('; ');

      const addr = parseAddress(c.address);

      return [
        c.company_name,
        addr.street,
        addr.houseNumber,
        addr.postalCode,
        addr.city,
        c.company_number,
        c.company_type,
        founders,
        c.founders?.length || 0,
        c.scrapeDate ? c.scrapeDate.split('T')[0] : '',
      ].map(escapeField).join(SEP);
    });

    const csv = '\uFEFF' + [headers.join(SEP), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    link.download = `companies_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-default-900">Companies</h1>
          <p className="text-default-500 mt-1">
            {filteredCompanies.length} companies{filteredCompanies.length !== allCompanies.length && ` (filtered from ${allCompanies.length})`}
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all font-medium ${
            showFilters || activeFilterCount > 0
              ? 'bg-primary-500 text-white border-primary-500'
              : 'bg-white text-default-600 border-default-200 hover:bg-default-50'
          }`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 bg-white/20 rounded-lg text-sm">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-card p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-default-600 mb-2">Search</label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-default-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Name, BTW, address..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                />
              </div>
            </div>

            {/* Company Type */}
            <div>
              <label className="block text-sm font-medium text-default-600 mb-2">Company Type</label>
              <select
                value={filters.companyType}
                onChange={(e) => setFilters(f => ({ ...f, companyType: e.target.value }))}
                className="w-full px-4 py-2.5 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm appearance-none cursor-pointer"
              >
                <option value="">All types</option>
                {companyTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Founder Count Range */}
            <div>
              <label className="block text-sm font-medium text-default-600 mb-2">Founders</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  placeholder="Min"
                  value={filters.founderCountMin}
                  onChange={(e) => setFilters(f => ({ ...f, founderCountMin: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                />
                <span className="text-default-400">-</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Max"
                  value={filters.founderCountMax}
                  onChange={(e) => setFilters(f => ({ ...f, founderCountMax: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="min-w-0">
              <label className="block text-sm font-medium text-default-600 mb-2">Date Range</label>
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                  className="min-w-0 flex-1 px-2 py-2.5 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                />
                <span className="text-default-400 flex-shrink-0">-</span>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                  className="min-w-0 flex-1 px-2 py-2.5 bg-default-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                />
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-default-100">
            <button
              onClick={clearFilters}
              disabled={activeFilterCount === 0}
              className="px-4 py-2.5 bg-default-100 text-default-600 rounded-xl hover:bg-default-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
            >
              Clear All
            </button>
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={filteredCompanies.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV ({filteredCompanies.length})
                <svg className={`w-3.5 h-3.5 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-default-200 overflow-hidden z-50">
                  <div className="px-3 py-2 border-b border-default-100">
                    <p className="text-xs font-medium text-default-400 uppercase tracking-wide">Sort by date</p>
                  </div>
                  <button
                    onClick={() => exportCsv('asc')}
                    className="w-full px-4 py-3 text-left text-sm text-default-700 hover:bg-default-50 flex items-center gap-3 transition-colors"
                  >
                    <svg className="w-4 h-4 text-default-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    Oldest first
                  </button>
                  <button
                    onClick={() => exportCsv('desc')}
                    className="w-full px-4 py-3 text-left text-sm text-default-700 hover:bg-default-50 flex items-center gap-3 transition-colors"
                  >
                    <svg className="w-4 h-4 text-default-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                    </svg>
                    Newest first
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card p-12 text-center">
          <div className="w-16 h-16 bg-default-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-default-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-default-900 mb-1">No companies found</h3>
          <p className="text-default-500">
            {activeFilterCount > 0 ? 'Try adjusting your filters' : 'Run a scrape to get started'}
          </p>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-all font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedDates.map((dateStr) => {
            const dateCompanies = groupedCompanies[dateStr];
            const isExpanded = expandedDates[dateStr];
            const visibleCount = visibleCounts[dateStr] || ITEMS_PER_LOAD;
            const visibleCompanies = dateCompanies.slice(0, visibleCount);
            const hasMore = dateCompanies.length > visibleCount;

            return (
              <div key={dateStr} className="bg-white rounded-2xl shadow-card overflow-hidden">
                {/* Date Header - Clickable */}
                <button
                  onClick={() => toggleDate(dateStr)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-default-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h2 className="font-semibold text-default-900 text-lg">{formatDateHeader(dateStr)}</h2>
                      <p className="text-sm text-default-500">{dateCompanies.length} companies</p>
                    </div>
                  </div>
                  <svg
                    className={`w-6 h-6 text-default-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Companies List */}
                {isExpanded && (
                  <div className="border-t border-default-100">
                    <div className="divide-y divide-default-100">
                      {visibleCompanies.map((c, i) => {
                        const companyKey = `${dateStr}-${c.filename || i}`;
                        const isCompanyExpanded = expandedCompany === companyKey;

                        return (
                          <div key={companyKey} className="hover:bg-default-50 transition-colors">
                            <div
                              className="px-5 py-4 cursor-pointer"
                              onClick={() => setExpandedCompany(isCompanyExpanded ? null : companyKey)}
                            >
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0">
                                  <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <span className="text-primary-600 font-bold">
                                      {c.company_name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-semibold text-default-900 truncate">{c.company_name}</span>
                                      {c.company_type && (
                                        <span className="px-2 py-0.5 bg-primary-500/10 text-primary-600 text-xs font-semibold rounded-lg flex-shrink-0">
                                          {c.company_type}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 text-sm text-default-500">
                                      {c.company_number && (
                                        <span className="flex items-center gap-1">
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                          </svg>
                                          {c.company_number}
                                        </span>
                                      )}
                                      {c.founders?.length > 0 && (
                                        <span className="flex items-center gap-1">
                                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                          </svg>
                                          {c.founders.length}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <svg
                                  className={`w-5 h-5 text-default-400 transition-transform flex-shrink-0 ${isCompanyExpanded ? 'rotate-180' : ''}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>

                            {/* Expanded Company Details */}
                            {isCompanyExpanded && (
                              <div className="px-5 pb-4">
                                <div className="bg-default-50 rounded-xl p-4 ml-14">
                                  {c.address && (
                                    <div className="mb-4">
                                      <p className="text-xs font-medium text-default-400 uppercase tracking-wide mb-1">Address</p>
                                      <p className="text-default-700">{c.address}</p>
                                    </div>
                                  )}

                                  <div>
                                    <p className="text-xs font-medium text-default-400 uppercase tracking-wide mb-2">Founders</p>
                                    {c.founders?.length > 0 ? (
                                      <div className="flex flex-wrap gap-2">
                                        {c.founders.map((f, fi) => {
                                          const founderName = typeof f === 'string' ? f : f.name;
                                          const founderTitle = typeof f === 'string' ? '' : f.title;
                                          return (
                                            <span
                                              key={fi}
                                              className="px-3 py-1.5 bg-white border border-default-200 rounded-lg text-sm text-default-700"
                                            >
                                              {founderTitle ? `${founderTitle} ` : ''}{founderName}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <p className="text-default-500 text-sm">No founders found</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Load More Button */}
                    {hasMore && (
                      <div className="px-5 py-4 border-t border-default-100 bg-default-50">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            loadMore(dateStr);
                          }}
                          className="w-full py-3 bg-white border border-default-200 rounded-xl text-default-600 hover:bg-default-100 transition-all font-medium flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          Load more ({dateCompanies.length - visibleCount} remaining)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Companies;
